import React, { useState } from "react";

import { Button, InfoMessage, Input } from "@amphore-dev/amphore-lib";
import { AxiosError } from "axios";
import { t } from "i18next";
import { Link, useLocation } from "react-router-dom";

import { useMutation } from "@tanstack/react-query";

import AuthService from "@/services/AuthService";

import { getApiErrormessage } from "@/utils/UAPIErrors";

import { TApiError } from "@/types";

/**
 * /forgot-password: the address, then always the same answer - the API
 * does not say whether it has an account, neither does this page.
 */
export const ForgotPassword: React.FC = () => {
	const location = useLocation();
	const [email, setEmail] = useState<string>(
		(location.state as { email?: string } | null)?.email ?? ""
	);

	const send = useMutation<void, AxiosError<TApiError>, string>({
		mutationFn: (address) => AuthService.forgotPassword(address),
	});

	if (send.isSuccess)
		return (
			<div className="flex flex-col gap-4" data-testid="forgot-sent">
				<InfoMessage color="success">
					{t("account.forgot.sent", { email })}
				</InfoMessage>
				<Link to="/login" className="text-center text-sm">
					{t("account.backToLogin")}
				</Link>
			</div>
		);

	return (
		<form
			className="flex flex-col gap-4"
			onSubmit={(e) => {
				e.preventDefault();
				if (email.trim()) send.mutate(email.trim());
			}}
		>
			<p className="text-sm text-neutral-600">
				{t("account.forgot.intro")}
			</p>
			<Input
				label={t("forms.email.email")}
				name="email"
				type="email"
				autoComplete="email"
				value={email}
				onChange={setEmail}
				disabled={send.isPending}
			/>
			{send.error && (
				<InfoMessage color="danger">
					{getApiErrormessage(send.error)}
				</InfoMessage>
			)}
			<Button
				type="submit"
				isLoading={send.isPending}
				disabled={!email.trim()}
			>
				{t("account.forgot.submit")}
			</Button>
			<Link to="/login" className="text-center text-sm">
				{t("account.backToLogin")}
			</Link>
		</form>
	);
};
